import { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Send, User, MessageCircle, AlertCircle, X, Bot, Trash2, Archive } from 'lucide-react'
import { db } from '../../firebase'
import { collection, query, where, onSnapshot, addDoc, serverTimestamp, updateDoc, doc, deleteDoc, getDocs } from 'firebase/firestore'
import { useToast } from '../../context/ToastContext'
import ConfirmModal from '../ConfirmModal'

const ChatSection = ({ staffMember }) => {
  const { showToast } = useToast()
  const [sessions, setSessions] = useState([])
  const [selectedSession, setSelectedSession] = useState(null)
  const [messages, setMessages] = useState([])
  const [inputMessage, setInputMessage] = useState('')
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, type: null, session: null })
  const [showArchived, setShowArchived] = useState(false)
  const messagesEndRef = useRef(null)

  const getInitials = (name) => {
    if (!name) return 'U'
    const parts = name.split(' ')
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase()
    }
    return name.substring(0, 2).toUpperCase()
  }

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' })
    }
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  // Listen for active chat sessions
  useEffect(() => {
    const sessionsRef = collection(db, 'chatSessions')
    const statusFilter = showArchived ? 'archived' : 'active'
    const q = query(sessionsRef, where('status', '==', statusFilter))

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const loadedSessions = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        startedAt: doc.data().startedAt?.toDate() || new Date()
      }))
      
      // Sort by most recent
      loadedSessions.sort((a, b) => b.startedAt - a.startedAt)
      setSessions(loadedSessions)
    })

    return () => unsubscribe()
  }, [showArchived])

  // Claim a chat session when staff clicks on it
  const handleSessionClick = async (session) => {
    // Check if chat is already assigned to another staff member
    if (session.assignedToStaff && session.assignedToStaff !== staffMember.id) {
      showToast(`Ky chat është marrë nga ${session.assignedToStaffName || 'një koleg'}`, 'warning')
      return
    }

    // Assign chat to current staff member if not already assigned
    if (!session.assignedToStaff && session.handoffRequested) {
      try {
        await updateDoc(doc(db, 'chatSessions', session.id), {
          assignedToStaff: staffMember.id,
          assignedToStaffName: staffMember.name,
          assignedAt: serverTimestamp(),
          handoffRequested: false // Clear the request flag
        })
        
        // Send automatic message to client that staff has joined
        await addDoc(collection(db, 'chatMessages'), {
          sessionId: session.sessionId,
          sender: 'bot',
          senderName: 'Sistemi',
          text: `${staffMember.name} u fut në bisedë dhe do t'ju ndihmojë tani! 👋`,
          timestamp: serverTimestamp()
        })
        
        showToast(`Ke marrë chat-in me ${session.userName}`, 'success')
      } catch (error) {
        console.error('Error assigning chat:', error)
        showToast('Gabim në marrjen e chat-it', 'error')
        return
      }
    }

    setSelectedSession(session)
  }

  const handleArchiveChat = async (sessionToArchive) => {
    try {
      await updateDoc(doc(db, 'chatSessions', sessionToArchive.id), {
        status: 'archived',
        archivedAt: serverTimestamp(),
        archivedBy: staffMember.id
      })
      
      if (selectedSession?.id === sessionToArchive.id) {
        setSelectedSession(null)
      }
      
      showToast('Chat-i u arkivua me sukses!', 'success')
    } catch (error) {
      console.error('Error archiving chat:', error)
      showToast('Gabim në arkivim!', 'error')
    }
  }

  // Listen for messages in selected session
  useEffect(() => {
    if (!selectedSession) return

    const messagesRef = collection(db, 'chatMessages')
    const q = query(
      messagesRef,
      where('sessionId', '==', selectedSession.sessionId)
    )

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const loadedMessages = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        time: doc.data().timestamp?.toDate().toLocaleTimeString('sq-AL', {
          hour: '2-digit',
          minute: '2-digit'
        }) || 'Tani',
        timestamp: doc.data().timestamp?.toDate() || new Date()
      }))
      
      loadedMessages.sort((a, b) => a.timestamp - b.timestamp)
      setMessages(loadedMessages)
    })

    return () => unsubscribe()
  }, [selectedSession])

  const handleSendMessage = async (e) => {
    e.preventDefault()
    e.stopPropagation()
    
    if (inputMessage.trim() && selectedSession) {
      // Check if current staff member is assigned to this chat
      if (selectedSession.assignedToStaff && selectedSession.assignedToStaff !== staffMember.id) {
        showToast('Nuk mund të dërgosh mesazhe në këtë chat. Ai është marrë nga një koleg.', 'error')
        return
      }

      try {
        await addDoc(collection(db, 'chatMessages'), {
          sessionId: selectedSession.sessionId,
          sender: 'staff',
          senderName: staffMember.name,
          text: inputMessage,
          timestamp: serverTimestamp(),
          read: false
        })
        setInputMessage('')
      } catch (error) {
        console.error('Error sending message:', error)
      }
    }
  }

  const handleCloseSession = async (sessionId) => {
    try {
      const sessionRef = doc(db, 'chatSessions', sessionId)
      await updateDoc(sessionRef, {
        status: 'closed',
        closedAt: serverTimestamp()
      })
      if (selectedSession?.id === sessionId) {
        setSelectedSession(null)
      }
      showToast('Sesioni u mbyll me sukses', 'success')
    } catch (error) {
      console.error('Error closing session:', error)
      showToast('Gabim gjatë mbylljes së sesionit', 'error')
    }
  }

  const handleDeleteChat = async (session) => {
    try {
      // Delete all messages for this session
      const messagesRef = collection(db, 'chatMessages')
      const q = query(messagesRef, where('sessionId', '==', session.sessionId))
      const messagesSnapshot = await getDocs(q)
      
      const deletePromises = messagesSnapshot.docs.map(doc => deleteDoc(doc.ref))
      await Promise.all(deletePromises)
      
      // Delete the session
      await deleteDoc(doc(db, 'chatSessions', session.id))
      
      if (selectedSession?.id === session.id) {
        setSelectedSession(null)
      }
      
      showToast('Chat-i u fshi me sukses', 'success')
    } catch (error) {
      console.error('Error deleting chat:', error)
      showToast('Gabim gjatë fshirjes së chat-it', 'error')
    }
  }

  const openCloseConfirm = (sessionId) => {
    setConfirmModal({ isOpen: true, type: 'close', sessionId })
  }

  const openDeleteConfirm = (session) => {
    setConfirmModal({ isOpen: true, type: 'delete', session })
  }

  const closeConfirmModal = () => {
    setConfirmModal({ isOpen: false, type: null, session: null, sessionId: null })
  }

  const confirmAction = () => {
    if (confirmModal.type === 'close' && confirmModal.sessionId) {
      handleCloseSession(confirmModal.sessionId)
    } else if (confirmModal.type === 'delete' && confirmModal.session) {
      handleDeleteChat(confirmModal.session)
    }
  }

  return (
    <div className="space-y-4">
      {/* Toggle between active and archived */}
      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-display font-bold text-gradient-gold">
          Live Chat Management
        </h2>
        <div className="flex gap-2">
          <button
            onClick={() => setShowArchived(false)}
            className={`px-4 py-2 rounded-lg transition-all ${
              !showArchived
                ? 'bg-gradient-gold text-white'
                : 'bg-bronze-900/30 text-beige-300 hover:bg-bronze-900/50'
            }`}
          >
            <MessageCircle className="w-4 h-4 inline mr-2" />
            Aktive ({sessions.length})
          </button>
          <button
            onClick={() => setShowArchived(true)}
            className={`px-4 py-2 rounded-lg transition-all ${
              showArchived
                ? 'bg-gradient-gold text-white'
                : 'bg-bronze-900/30 text-beige-300 hover:bg-bronze-900/50'
            }`}
          >
            <Archive className="w-4 h-4 inline mr-2" />
            Arkivi
          </button>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-4 lg:gap-6 h-auto lg:h-[calc(100vh-250px)]">
        {/* Sessions List */}
        <div className="w-full lg:w-80 bg-bronze-900/30 backdrop-blur-sm border border-gold-500/20 rounded-2xl p-4 overflow-y-auto max-h-[40vh] lg:max-h-none">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-display font-bold text-gold-400 flex items-center">
              {showArchived ? (
                <>
                  <Archive className="w-5 h-5 mr-2" />
                  Arkivi ({sessions.length})
                </>
              ) : (
                <>
                  <MessageCircle className="w-5 h-5 mr-2" />
                  Chat Aktive ({sessions.length})
                </>
              )}
            </h3>
          </div>

        <div className="space-y-2">
          {sessions.length === 0 ? (
            <div className="text-center py-8">
              <AlertCircle className="w-12 h-12 text-beige-400 mx-auto mb-3 opacity-50" />
              <p className="text-beige-400 text-sm">Nuk ka sesione aktive</p>
            </div>
          ) : (
            sessions.map((session) => {
              const isAssignedToMe = session.assignedToStaff === staffMember.id
              const isAssignedToOther = session.assignedToStaff && session.assignedToStaff !== staffMember.id
              const isRequested = session.handoffRequested && !session.assignedToStaff
              
              return (
                <motion.div
                  key={session.id}
                  layout
                  className={`relative group p-4 rounded-xl border cursor-pointer transition-all ${
                    selectedSession?.id === session.id
                      ? 'bg-gradient-gold text-white border-gold-400'
                      : isRequested
                      ? 'bg-yellow-500/10 hover:bg-yellow-500/20 text-cream border-yellow-500/40 animate-pulse'
                      : isAssignedToOther
                      ? 'bg-red-500/10 text-beige-400 border-red-500/20 opacity-60 cursor-not-allowed'
                      : 'bg-dark/30 hover:bg-dark/50 text-cream border-gold-500/20'
                  }`}
                  onClick={() => handleSessionClick(session)}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <User className="w-4 h-4" />
                      <span className="font-semibold">{session.userName}</span>
                      {isRequested && (
                        <span className="text-xs bg-yellow-400 text-dark px-2 py-0.5 rounded-full font-bold">
                          NË PRITJE
                        </span>
                      )}
                      {isAssignedToMe && (
                        <span className="text-xs bg-green-400 text-dark px-2 py-0.5 rounded-full font-bold">
                          I YTTI
                        </span>
                      )}
                      {isAssignedToOther && (
                        <span className="text-xs bg-red-400 text-white px-2 py-0.5 rounded-full font-bold">
                          I ZËNË
                        </span>
                      )}
                    </div>
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                  </div>
                  <p className="text-xs opacity-80 truncate">{session.userEmail}</p>
                  {isAssignedToOther && (
                    <p className="text-xs text-red-400 mt-1">👤 {session.assignedToStaffName}</p>
                  )}
                  <p className="text-xs opacity-60 mt-1">
                    {session.startedAt.toLocaleTimeString('sq-AL', { 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })}
                  </p>
                
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    if (!showArchived) {
                      handleArchiveChat(session)
                    }
                  }}
                  className="absolute top-2 right-16 opacity-0 group-hover:opacity-100 p-1 hover:bg-gold-500/20 rounded transition-all"
                  title={showArchived ? "Në arkiv" : "Arkivo chat-in"}
                >
                  <Archive className="w-4 h-4 text-gold-400" />
                </button>
                
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    openCloseConfirm(session.id)
                  }}
                  className="absolute top-2 right-8 opacity-0 group-hover:opacity-100 p-1 hover:bg-yellow-500/20 rounded transition-all"
                  title="Mbyll sesionin"
                >
                  <X className="w-4 h-4 text-yellow-400" />
                </button>
                
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    openDeleteConfirm(session)
                  }}
                  className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 p-1 hover:bg-red-500/20 rounded transition-all"
                  title="Fshi chat-in"
                >
                  <Trash2 className="w-4 h-4 text-red-400" />
                </button>
              </motion.div>
              )
            })
          )}
        </div>
      </div>

      {/* Chat Area */}
      <div className="flex-1 flex flex-col bg-bronze-900/30 backdrop-blur-sm border border-gold-500/20 rounded-2xl overflow-hidden h-[60vh] lg:h-auto">
        {selectedSession ? (
          <>
            {/* Chat Header */}
            <div className="bg-gradient-gold p-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-bronze-900" />
                </div>
                <div>
                  <h4 className="text-white font-semibold">{selectedSession.userName}</h4>
                  <p className="text-white/80 text-sm">{selectedSession.userEmail}</p>
                </div>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 p-4 lg:p-6 overflow-y-auto" style={{ scrollBehavior: 'smooth' }}>
              <AnimatePresence>
                {messages.map((message) => (
                  <motion.div
                    key={message.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`flex mb-4 ${
                      message.sender === 'user' ? 'justify-end' : 'justify-start'
                    }`}
                  >
                    <div className={`max-w-[70%] ${message.sender === 'user' ? 'order-2' : 'order-1'}`}>
                      {/* Show sender info for bot/staff */}
                      {message.sender !== 'user' && (
                        <div className="flex items-center space-x-2 mb-1">
                          {message.sender === 'bot' ? (
                            <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                              <Bot className="w-4 h-4 text-white" />
                            </div>
                          ) : (
                            <div className="w-6 h-6 bg-gradient-gold rounded-full flex items-center justify-center text-white text-xs font-bold">
                              {getInitials(message.senderName)}
                            </div>
                          )}
                          <span className="text-xs text-beige-300">{message.senderName}</span>
                        </div>
                      )}
                      
                      <div className={`rounded-2xl p-3 ${
                        message.sender === 'user'
                          ? 'bg-gradient-gold text-white'
                          : message.sender === 'bot'
                          ? 'bg-blue-900/50 border border-blue-500/30 text-cream'
                          : 'bg-bronze-900/50 border border-gold-500/20 text-cream'
                      }`}>
                        <p className="text-sm whitespace-pre-line">{message.text}</p>
                      </div>
                      <p className={`text-xs text-beige-400 mt-1 ${
                        message.sender === 'user' ? 'text-right' : 'text-left'
                      }`}>
                        {message.time}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <form onSubmit={handleSendMessage} className="p-4 border-t border-gold-500/20">
              <div className="flex items-center space-x-3">
                <input
                  type="text"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      handleSendMessage(e)
                    }
                  }}
                  placeholder="Shkruaj përgjigjen..."
                  className="flex-1 px-4 py-3 bg-dark/50 border border-gold-500/30 rounded-xl text-cream placeholder-beige-400 focus:border-gold-400 focus:ring-2 focus:ring-gold-500/20 transition-all outline-none"
                />
                <button
                  type="submit"
                  disabled={!inputMessage.trim()}
                  className="btn-primary p-3 disabled:opacity-50"
                >
                  <Send className="w-5 h-5" />
                </button>
              </div>
            </form>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <MessageCircle className="w-16 h-16 text-beige-400 mx-auto mb-4 opacity-50" />
              <p className="text-beige-300">Zgjidh një sesion për të filluar</p>
            </div>
          </div>
        )}
      </div>
      </div>

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={closeConfirmModal}
        onConfirm={confirmAction}
        title={confirmModal.type === 'close' ? 'Mbyll Sesionin' : 'Fshi Chat-in'}
        message={
          confirmModal.type === 'close'
            ? 'Dëshiron ta mbyllësh këtë sesion? Klienti nuk do të mund të dërgojë më mesazhe.'
            : `Dëshiron ta fshish këtë chat me të gjitha mesazhet? Ky veprim nuk mund të zhbëhet.`
        }
        confirmText={confirmModal.type === 'close' ? 'Po, Mbyll' : 'Po, Fshi'}
        cancelText="Jo, Anulo"
      />
    </div>
  )
}

export default ChatSection
